import type { ReactNode, Ref, SelectHTMLAttributes } from 'react';
import { ErrorMessage } from './ErrorMessage';
import { Hint } from './Hint';
import { Label } from './Label';
import { RequiredMarker } from './RequiredMarker';
import { cx, describedBy, widthClass, type FluidWidth } from './cx';

export interface SelectOption {
  value: string;
  label: string;
  lang?: string;
  disabled?: boolean;
  /** Label of an optgroup; consecutive options with the same group are rendered in one optgroup. */
  group?: string;
}

function renderOption(o: SelectOption) {
  return (
    <option key={o.value} value={o.value} lang={o.lang} disabled={o.disabled}>
      {o.label}
    </option>
  );
}

function renderOptions(options: SelectOption[]) {
  const runs: { group?: string; options: SelectOption[] }[] = [];
  for (const o of options) {
    const last = runs.at(-1);
    if (last && last.group === o.group) last.options.push(o);
    else runs.push({ group: o.group, options: [o] });
  }
  return runs.map((run, i) =>
    run.group === undefined ? (
      run.options.map(renderOption)
    ) : (
      <optgroup key={`${run.group}-${i}`} label={run.group}>
        {run.options.map(renderOption)}
      </optgroup>
    ),
  );
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  id: string;
  label: ReactNode;
  labelClassName?: string;
  labelLang?: string;
  hint?: ReactNode;
  hintLang?: string;
  error?: string;
  /** a red asterisk after the label / legend ("(povinné)" for screen readers) */
  required?: boolean;
  options: SelectOption[];
  /** Text of an empty first option ("Vyberte…"); omitted when undefined. */
  placeholder?: string;
  width?: FluidWidth;
  formGroupClassName?: string;
  extraDescribedBy?: string;
  beforeInput?: ReactNode;
  ref?: Ref<HTMLSelectElement>;
}

/** govuk/components/select */
export function Select({
  id,
  label,
  labelClassName,
  labelLang,
  hint,
  hintLang,
  error,
  required,
  options,
  placeholder,
  width,
  formGroupClassName,
  extraDescribedBy,
  beforeInput,
  className,
  ref,
  ...rest
}: SelectProps) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  return (
    <div className={cx('govuk-form-group', error && 'govuk-form-group--error', formGroupClassName)}>
      <Label htmlFor={id} className={labelClassName} lang={labelLang}>
        {label}
        <RequiredMarker required={required} />
      </Label>
      {hint && (
        <Hint id={hintId} lang={hintLang}>
          {hint}
        </Hint>
      )}
      {error && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
      {beforeInput}
      <select
        ref={ref}
        className={cx('govuk-select', widthClass(width, 'select'), className, error && 'govuk-select--error')}
        id={id}
        aria-describedby={describedBy(extraDescribedBy, hintId, errorId)}
        aria-invalid={error ? true : undefined}
        {...rest}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {renderOptions(options)}
      </select>
    </div>
  );
}
